import { z } from "zod";
/**
 * A company the answers keep naming that nobody is tracking, and that is a rival.
 *
 * The record of every named company makes this askable for the first time. A
 * rival can be named in every answer we capture and be invisible on every
 * screen, because before this nothing was written down about companies outside
 * the brand list.
 */
export const discoveredCompanySchema = z.object({
    /** Normalised, and the key a backfill matches on when they are added. */
    entityKey: z.string(),
    /** The label the engines used most often for them. */
    name: z.string(),
    /**
     * How many of this report's answers named them in prose, in the last 90 days.
     *
     * Every answer was read for every company it named, so this is also what they
     * arrive with if they are added: the observations were written whether or not
     * anyone was watching.
     */
    mentions: z.number().int().min(0),
    /**
     * A site one of these answers cited whose name is this company's, or null.
     *
     * Only ever used to draw their logo. It is **not** the website they are added
     * with — that is still typed by a person, because attaching a rival's history
     * to the wrong company is the one mistake here nothing downstream would show.
     *
     * Null is the ordinary state and means "no answer that named them also linked
     * to a site we can prove is theirs". Drawn as initials, which is the honest
     * rendering of a company we can name and cannot identify.
     */
    iconDomain: z.string().nullable(),
});
export const discoveredCompaniesSchema = z.object({
    /**
     * Only names judged to be this brand's competitors, most named first. A name
     * the answers keep using is not thereby a rival, so nothing unjudged is here.
     */
    companies: z.array(discoveredCompanySchema),
    /**
     * False when no model is configured, so nothing could be judged. A different
     * fact from an empty list, and it must not be drawn as one (§1).
     */
    verified: z.boolean(),
});
/**
 * A name the answers keep using that looks like a brand already tracked.
 *
 * Offered, never applied. Approving it writes an alias onto the brand, and
 * every mention afterwards is matched by the deterministic matcher — so the
 * model's contribution lands in configuration a person can see and delete
 * rather than in a measurement nobody can audit (§14a).
 */
export const aliasCandidateSchema = z.object({
    /** The name as the answers wrote it. */
    observed: z.string(),
    brandId: z.string(),
    brandName: z.string(),
    /** Stored mentions that approving it would claim. */
    mentions: z.number().int().min(0),
});
export const aliasCandidatesSchema = z.object({
    candidates: z.array(aliasCandidateSchema),
    /**
     * Null when no model is configured.
     *
     * A different fact from "no candidates", and only one of them is fixed by
     * setting a key. An empty list in both cases would read as "every name is
     * already understood" (§1).
     */
    model: z.string().nullable(),
});
export const approveAliasSchema = z.object({
    brandId: z.string().min(1),
    /** The observed name to add as an alias, exactly as it was offered. */
    alias: z.string().min(1),
});
export const approveAliasResultSchema = z.object({
    /** Observations linked to the brand by approving it. */
    linked: z.number().int().min(0),
});
//# sourceMappingURL=discovered.js.map